import { Document, DocumentData, DocumentState, DocumentDataToDocumentState } from "./document";
import { StateEmitterArray } from "../../../../emitter/state-emitter-array";

import { db } from "./db";

import { firebase } from "../platform-firebase-types";

const debug = false;
export interface Where {
  field: string;
  op: WhereFilterOp;
  value: any;
}
export type WhereFilterOp = firebase.firestore.WhereFilterOp;

export interface OrderBy {
  field: string;
  direction: OrderByDirection;
}
export type OrderByDirection = firebase.firestore.OrderByDirection;

/**
 * A generic Collection proxy class for Firestore collections.
 */
export class Collection<T extends Document<S>, S extends DocumentState> extends StateEmitterArray<
  T,
  S
> {
  private _collectionPath: string | undefined;
  private unsubscribe: (() => void) | undefined;

  protected static addDocument(collectionPath: string, data: DocumentData): Promise<string> {
    return db()
      .collection(collectionPath)
      .add(data)
      .then(docRef => {
        debug && console.log(`Collection[${collectionPath}].addDocument success ${docRef.id}`);
        return docRef.id;
      })
      .catch(error => {
        debug && console.error(`Collection[${collectionPath}].addDocument:`, error);
        throw error;
      });
  }

  protected static getCollection<S>(
    documentConverter: DocumentDataToDocumentState<S>,
    collectionPath: string,
    where?: Array<Where>,
    orderBy?: Array<OrderBy>,
    limit?: number
  ): Promise<Array<S>> {
    const query = this.buildQuery(db().collection(collectionPath), where, orderBy, limit);
    return query
      .get()
      .then(querySnapshot => {
        debug && console.log(`Collection[${collectionPath}].getCollection success`);
        const result: Array<S> = [];
        querySnapshot.forEach(doc => result.push(documentConverter(doc.id, doc.data())));
        return result;
      })
      .catch(error => {
        debug && console.log(`Collection[${collectionPath}].getCollection error`, error);
        throw error;
      });
  }

  private static buildQuery(
    query: firebase.firestore.Query,
    where?: Array<Where>,
    orderBy?: Array<OrderBy>,
    limit?: number
  ): firebase.firestore.Query {
    if (where) {
      where.forEach(whereObj => {
        query = query.where(whereObj.field, whereObj.op, whereObj.value);
      });
    }
    if (orderBy) {
      orderBy.forEach(orderByObj => {
        query = query.orderBy(orderByObj.field, orderByObj.direction);
      });
    }
    if (limit) {
      query = query.limit(limit);
    }
    return query;
  }

  protected constructor(private documentObjectType: new (state: S) => T) {
    super();
  }

  dispose(): void {
    this.publishComplete();
    this.stopListening();
  }

  get collectionPath(): string | undefined {
    return this._collectionPath;
  }

  protected startListening(
    documentConverter: DocumentDataToDocumentState<S>,
    collectionPath: string,
    where?: Array<Where>,
    orderBy?: Array<OrderBy>,
    limit?: number
  ): void {
    debug && console.log(`Collection[${collectionPath}].startListening`);
    this._collectionPath = collectionPath;
    this.stopListening();
    const query = Collection.buildQuery(db().collection(collectionPath), where, orderBy, limit);
    this.unsubscribe = query.onSnapshot(
      snapshot => {
        debug && console.log(`Collection[${collectionPath}].listening update`);
        snapshot.docChanges().forEach(change => {
          this.handleChange(change, documentConverter);
        });
      },
      error => {
        debug && console.error(`Collection[${collectionPath}].listening error`, error);
        this.unsubscribe = undefined;
        this.publishStateError(error);
      },
      () => {
        // should never happen
        debug && console.error(`Collection[${collectionPath}].listening complete`);
        this.unsubscribe = undefined;
        this.publishComplete();
      }
    );
  }

  protected stopListening(): void {
    debug && console.log(`Collection[${this._collectionPath}].stopListening`);
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = undefined;
      this._collectionPath = undefined;
      while (this.array.length) this.remove(this.array[0]); // careful, remove uses splice()
    }
  }

  private handleChange(
    change: firebase.firestore.DocumentChange,
    documentConverter: DocumentDataToDocumentState<S>
  ): void {
    switch (change.type) {
      case "added":
        this.handleChangeAdded(change, documentConverter);
        break;
      case "modified":
        this.handleChangeModified(change, documentConverter);
        break;
      case "removed":
        this.handleChangeRemoved(change);
        break;
      default:
        throw new Error("Unknown document change type: " + change.type);
    }
  }

  private handleChangeAdded(
    change: firebase.firestore.DocumentChange,
    documentConverter: DocumentDataToDocumentState<S>
  ): void {
    const state = documentConverter(change.doc.id, change.doc.data());
    this.add(new this.documentObjectType(state));
  }

  private handleChangeModified(
    change: firebase.firestore.DocumentChange,
    documentConverter: DocumentDataToDocumentState<S>
  ): void {
    const state = documentConverter(change.doc.id, change.doc.data());
    const element = this.array.find(elem => elem.stateValue.id === change.doc.id);
    if (element === undefined) {
      throw new Error("Failed to find element to update " + change.doc.id);
    }
    element.update(state);
  }

  private handleChangeRemoved(change: firebase.firestore.DocumentChange): void {
    const element = this.array.find(elem => elem.stateValue.id === change.doc.id);
    if (element === undefined) {
      throw new Error("Failed to find element to remove " + change.doc.id);
    }
    this.remove(element);
  }
}
